import { ChatBubbleOutline } from "@mui/icons-material";
import { Box, IconButton, Popover, TextField, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const EvidenceCommentButton = ({ evidence, isEditable, onSave, disabled }) => {
  const [anchorEl, setAnchorEl] = useState(null);  
  const [saving, setSaving] = useState(false);
  const [comment, setComment] = useState("");
  const { t } = useTranslation();

  useEffect(() => {
    setComment(evidence.comment ?? "");
  }, [evidence.comment]);

  // const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleOpen = (event) => {
    if (disabled) return; // prevent opening if disabled
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleSave = async (newComment) => {
    // compare against original value from props
    if (comment === (evidence.comment || "")) return;
    setSaving(true);
    try {
      await onSave(newComment); // call parent-provided function
      setComment(newComment);
    } catch (err) {
      console.error(t("user.evidenceProvider.commentError"), err);
    } finally {
      setSaving(false);
    }
  };

  const isOpen = Boolean(anchorEl);

  return (
    <>
      <IconButton size="small" onClick={handleOpen} disabled={saving}>
        <ChatBubbleOutline fontSize="small" color={comment ? "primary" : "inherit"} />
      </IconButton>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            backgroundColor: "#fff !important",
            boxShadow: "0px 8px 24px rgba(0,0,0,0.2)",
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            width: 320,
            zIndex: 1300,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
          {t("user.evidenceProvider.comment")}
          </Typography>

          {isEditable ? (
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              size="small"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onBlur={() => handleSave(comment)}
              placeholder="Write a comment..."
              autoFocus
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fafafa" } }}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{
                p: 1.5,
                bgcolor: "#f5f5f5",
                borderRadius: 1,
                color: "grey.700",
                minHeight: 60,
                whiteSpace: "pre-wrap",
              }}
            >
              {comment || "No comments added yet."}
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
};

EvidenceCommentButton.propTypes = {
  evidence: PropTypes.shape({
    evidenceId: PropTypes.number.isRequired,
    employeeId: PropTypes.string.isRequired,
    month: PropTypes.number.isRequired,
    year: PropTypes.number.isRequired,
    comment: PropTypes.string,
  }).isRequired,
  isEditable: PropTypes.bool,
  onSave: PropTypes.func.isRequired, // parent handles API call
  disabled: PropTypes.bool,
};

EvidenceCommentButton.defaultProps = {
  isEditable: true,
  disabled: false,
};

export default EvidenceCommentButton;
